import utils from '../utils';

export interface RhythmicSolfegeSystem {
  simple: {
    firstLevelSubdivisions: [string, string];
    smallSubdivisions: [string, string, string, string];
  };
  compound: {
    firstLevelSubdivisions: [string, string, string];
    smallSubdivisions: [string, string, string, string, string, string];
  };
}

export const RhythmicSolfegeSystems: Readonly<{
  [systemName: string]: RhythmicSolfegeSystem;
}> = Object.freeze({
  '1e&a': {
    simple: {
      firstLevelSubdivisions: ['1', '&'],
      smallSubdivisions: ['1', 'e', '&', 'a'],
    },
    compound: {
      firstLevelSubdivisions: ['1', 'la', 'li'],
      smallSubdivisions: ['1', 'ta', 'la', 'ta', 'li', 'ta'],
    },
  },
  takadimi: {
    simple: {
      firstLevelSubdivisions: ['ta', 'di'],
      smallSubdivisions: ['ta', 'ka', 'di', 'mi'],
    },
    compound: {
      firstLevelSubdivisions: ['ta', 'ki', 'da'],
      smallSubdivisions: ['ta', 'va', 'ki', 'di', 'da', 'ma'],
    },
  },
});

export function getBeatSyllablesTemplate(
  systemOrSystemName: RhythmicSolfegeSystem | '1e&a' | 'takadimi',
  isSimple: boolean,
  isSmallSubdivision: boolean
): string[] {
  const system = isRhythmicSolfegeSystem(systemOrSystemName)
    ? systemOrSystemName
    : RhythmicSolfegeSystems[systemOrSystemName];
  return system[isSimple ? 'simple' : 'compound'][
    isSmallSubdivision ? 'smallSubdivisions' : 'firstLevelSubdivisions'
  ];
}

export function getSyllable(
  /** Zero-based */
  subdivisionNumber: number,
  systemOrSystemName: RhythmicSolfegeSystem | '1e&a' | 'takadimi',
  isSimple: boolean,
  smallSubdivisions: boolean
): string {
  const template = getBeatSyllablesTemplate(systemOrSystemName, isSimple, smallSubdivisions);
  const index = subdivisionNumber % template.length;
  if (index === 0 && utils.isNumeric(template[0])) {
    return `${subdivisionNumber / template.length + 1}`;
  }
  return template[index];
}

export function getSyllableFactory(
  systemOrSystemName: RhythmicSolfegeSystem | '1e&a' | 'takadimi',
  isSimple: boolean,
  smallSubdivisions: boolean
): (number) => string {
  return (subdivisionNumber: number) =>
    getSyllable(subdivisionNumber, systemOrSystemName, isSimple, smallSubdivisions);
}

export function isRhythmicSolfegeSystem(system: any): system is RhythmicSolfegeSystem {
  if (!utils.isObject(system)) return false;
  const solfegeSystem = system as RhythmicSolfegeSystem;
  if (!utils.isObject(solfegeSystem.simple)) return false;
  if (!utils.isArray(solfegeSystem.simple.firstLevelSubdivisions)) return false;
  if (!allString(solfegeSystem.simple.firstLevelSubdivisions)) return false;
  if (solfegeSystem.simple.firstLevelSubdivisions.length !== 2) return false;
  if (!utils.isArray(solfegeSystem.simple.smallSubdivisions)) return false;
  if (!allString(solfegeSystem.simple.smallSubdivisions)) return false;
  if (solfegeSystem.simple.smallSubdivisions.length !== 4) return false;

  if (!utils.isArray(solfegeSystem.compound.firstLevelSubdivisions)) return false;
  if (!allString(solfegeSystem.compound.firstLevelSubdivisions)) return false;
  if (solfegeSystem.compound.firstLevelSubdivisions.length !== 3) return false;
  if (!utils.isArray(solfegeSystem.compound.smallSubdivisions)) return false;
  if (!allString(solfegeSystem.compound.smallSubdivisions)) return false;
  if (solfegeSystem.compound.smallSubdivisions.length !== 6) return false;

  return true;
}

function allString(array: any[]) {
  return !array.find((el) => !utils.isString(el));
}
